import React from "react";
import { type NextPage } from "next";
import { getLandingPageProps, getPageMetaImage, type LandingPageProps } from "utils";
import { Section, sectionSpacingY } from "components/section";
import { Page } from "components/page";
import { CenteredQuote } from "components/quotes";
import { Customers } from "components/logos";
import { CustomerStats, DownloadWhitepaper, SecurityFeatures } from "components/content";
import { AuraPlate } from "ui/plates";
import { GradientHalfHero } from "components/hero";
import Image from "next/image";
import { Diptych, DiptychColumn } from "ui/diptych";
import { Box } from "@chakra-ui/react";
import { Button } from "ui/button";
import { integrationsCount } from "../../constants/integrations";
import heroImage from "../../../public/assets/solutions/media/hero.png";
import firstPairImage from "../../../public/assets/solutions/media/pair-1.png";
import secondPairImage from "../../../public/assets/solutions/media/pair-2.png";
import thirdPairImage from "../../../public/assets/solutions/media/pair-3.png";

const Media: NextPage<LandingPageProps> = ({ page, announcementBanner, customers, embeddedWhitepaper }) => {
  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
      bgColor="new.blue.150"
      footerOptions={{ variant: "blue" }}
    >
      <GradientHalfHero
        variant="blue"
        eyebrow={page.eyebrow}
        title={page.title}
        description={page.description}
        media={<Image priority sizes="100%" src={heroImage} alt="" style={{ objectFit: "cover" }} />}
        gap={{ base: "biggest", xl: "huge" }}
      />

      {page.customers && <Customers customers={page.customers} my={0} mb="huger" />}

      <CustomerStats customers={customers} my={0} mb="huger" />

      {embeddedWhitepaper && <DownloadWhitepaper variant="blue" {...embeddedWhitepaper} />}

      <Section my="0" mt="huger" pb={sectionSpacingY}>
        <DiptychColumn>
          <Diptych
            direction="left"
            mediaRatio={534 / 413}
            media={
              <Box>
                <Image src={firstPairImage} alt="" style={{ transform: "scale(1.02)" }} />
              </Box>
            }
            title="Earn more from advertising placements"
            description="Drive more revenue by enhancing the targeting of your retail media placements with your first-party data. Always serve the most relevant content at the right time."
          />

          <Diptych
            direction="right"
            mediaRatio={534 / 413}
            media={
              <Box>
                <Image src={secondPairImage} alt="" style={{ transform: "scale(1.02)" }} />
              </Box>
            }
            title="Hyper-personalize customer journeys"
            description={`Leverage your customer data to personalize every customer interaction across your website, email, ads, and ${integrationsCount}+ other Hightouch destinations. Media recommendations made easy.`}
          />

          <Diptych
            direction="left"
            mediaRatio={534 / 413}
            media={
              <Box>
                <Image src={thirdPairImage} alt="" style={{ transform: "scale(1.02)" }} />
              </Box>
            }
            title="Centralize audience management"
            description="Build and sync audiences to any marketing tool or ad platform with a no-code audience builder. Target your most valuable customers across channels. No CSV uploads required."
            actions={
              <Button hasRightChevron href="/platform/customer-studio">
                Explore Customer Studio
              </Button>
            }
          />
        </DiptychColumn>
      </Section>

      <AuraPlate condensed variant="blue">
        <CenteredQuote
          maxW="800px"
          quoteText="The combination of Snowflake and Hightouch forms the cornerstone of our customer intelligence platform."
          author={{
            name: "Tom Dinneny",
            job: "Customer Intelligence Platform Director",
          }}
          company={{
            name: "Warner Music Group",
            logo: "https://cdn.sanity.io/images/pwmfmi47/production/84dbb616e064de970801b02e16e419d60fcd4b44-2560x1009.png",
            logoRatio: 3 / 1,
          }}
          cta={{ href: "/customers/wmg", text: "Read customer story" }}
        />
      </AuraPlate>

      <SecurityFeatures my={0} py={sectionSpacingY} />
    </Page>
  );
};

export const getStaticProps = getLandingPageProps("/solutions/media", {
  loadCustomers: ["wmg", "auto-trader", "minno"],
  loadEmbeddedWhitepaper: "media-cdp-guide",
});

export default Media;
